import { useMemo, type ReactElement } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { EMAIL_MODULE_KEY } from "@playbookai/types";
import {
  Boxes,
  Brain,
  Building2,
  ChartNoAxesCombined,
  KeyRound,
  Mail,
  Plug,
  ScrollText,
  Users,
} from "lucide-react";
import { SettingsLayout, type SettingsNavItem } from "@playbookai/ui";
import { ADMIN_SECTION_ROUTES } from "../../routes.js";
import {
  getTenantConfiguration,
  tenantConfigurationQueryKey,
} from "../../api/tenant-config-api.js";
import { useAuthStore } from "../../auth/auth-store.js";
import {
  SettingsLeaveGuardProvider,
  useGuardedNavigate,
} from "../../settings/leave-guard.js";

/**
 * Admin sections that a tenant module governs, section id -> catalogue key.
 *
 * A disabled module has to take its ADMIN surface with it, not just its product
 * routes: leaving "Email & notifications" in the rail after an administrator
 * turned Email off is the same page still offering to configure a mail server
 * for mail that will not be sent.
 *
 * The rail is otherwise deliberately unfiltered: `/admin` is gated once at the
 * parent by RequireTenantAdmin, so no entry here carries a role branch.
 */
const MODULE_GATED_SECTIONS: Readonly<Record<string, string>> = {
  communication: EMAIL_MODULE_KEY,
};

/* Admin console host wiring (PB-42). Tenant administration moved out of
   `/settings` to a top-level `/admin` area: the whole route tree is gated
   once at the parent by RequireTenantAdmin (App.tsx), so — unlike
   SettingsPage — the nav registry carries no role branch. `/settings` keeps
   the personal sections (plus KPI Manager, the deliberate exception). The
   active child route renders via <Outlet/>. */

/* Grouped because PB-1588's four domains take the rail past six entries, which
   is where a flat list stops being scannable. Items are listed in render order
   and SettingsLayout emits a heading when the group changes, so entries sharing
   a group must stay adjacent here.

   Members stays first within the first group: "where do I manage people?" is
   the first thing an admin looks for (PB-51), and `/admin`'s index redirects
   there, so it must remain the top entry. */
export const ADMIN_NAV: readonly SettingsNavItem[] = [
  {
    id: "members",
    label: "Members & roles",
    description: "Provisioning, roles & access",
    icon: Users,
    group: "Access",
  },
  /* Read-only status (PB-49): identity-provider configuration is owned by the
     deployment, so this section confirms rather than edits. */
  {
    id: "identity",
    label: "Identity & directory",
    description: "Sign-in & projection status",
    icon: KeyRound,
    group: "Access",
  },
  {
    id: "organization",
    label: "Organization",
    description: "Branding & profile",
    icon: Building2,
    group: "Workspace",
  },
  {
    id: "modules",
    label: "Modules",
    description: "Module availability",
    icon: Boxes,
    group: "Workspace",
  },
  /* Email & notifications (PB-2925 / AC-14) sits with the other things an
     administrator SETS UP, which is what the Workspace group collects. */
  {
    id: "communication",
    label: "Email & notifications",
    description: "Mail server & delivery",
    icon: Mail,
    group: "Workspace",
  },
  /* Configures AIS (PB-1588 domain 2). Not a second path to a model — the
     page names no model family and carries no prompt text. */
  {
    id: "genai",
    label: "GenAI configuration",
    description: "Model connections & credentials",
    icon: Brain,
    group: "Platform",
  },
  /* Integrations (PB-4624, moved here from `/settings/integrations` where
     PB-1591 first shipped it). Beside GenAI configuration because it is the
     same kind of thing — configuring this deployment's link to an outside
     system — and before Usage & health, which reports on both.

     The label and description name no provider, for the same reason the page
     does not: which providers exist is a property of the deployment's
     registered connectors, not of the shell. */
  {
    id: "integrations",
    label: "Integrations",
    description: "Connected systems & access",
    icon: Plug,
    group: "Platform",
  },
  /* Read-only product adoption. Operational health remains in Datadog; this
     page answers how teams are using the workspace, from module-owned data. */
  {
    id: "health",
    label: "Adoption",
    description: "Workspace activity & trends",
    icon: ChartNoAxesCombined,
    group: "Platform",
  },
  /* Read-only (PB-1588 domain 1): `audit_log` has UPDATE/DELETE revoked at the
     database level (ADR-0005), so this section reports and never edits. */
  {
    id: "audit",
    label: "Audit & compliance",
    description: "Workspace-wide audit history",
    icon: ScrollText,
    group: "Governance",
  },
];

/** "/admin" → members (the index redirect target); "/admin/<segment>" → that
   section id. */
function activeIdFromPath(pathname: string): string {
  const segment = pathname.replace(/\/+$/, "").split("/")[2];
  return segment ?? "members";
}

function AdminPageHost(): ReactElement {
  const navigate = useGuardedNavigate();
  const location = useLocation();
  const tenantId = useAuthStore((state) => state.claims?.tenantId ?? null);
  /* The CATALOGUE, not `claims.enabledModules`, and the difference is the whole
     point: the claim carries only the keys that are ENABLED, so a key the
     deployment has not seeded yet is indistinguishable from one an
     administrator switched off. Gating on the claim would therefore hide this
     section on any deployment whose module rows have not been created — an
     outage of the page you would use to diagnose it.
   *
     The catalogue read answers the question properly, because it lists every
     module WITH its status. Absent, pending or failed all mean "show it": this
     is a courtesy gate, and the server-side send check is what actually stops
     mail. Same key as the Modules page, so it is one cached read, not two. */
  const tenantConfig = useQuery({
    queryKey: tenantConfigurationQueryKey(tenantId),
    queryFn: getTenantConfiguration,
  });

  const disabledKeys = useMemo(() => {
    const disabled = new Set<string>();
    for (const module of tenantConfig.data?.modules ?? []) {
      if (module.status !== "enabled") disabled.add(module.moduleKey);
    }
    return disabled;
  }, [tenantConfig.data]);

  const items = useMemo(
    () =>
      ADMIN_NAV.filter((item) => {
        const key = MODULE_GATED_SECTIONS[item.id];
        return key === undefined || !disabledKeys.has(key);
      }),
    [disabledKeys],
  );

  /* A hidden rail entry is not a gate — the URL is still typeable, and the
     section may be open when the module is turned off in another tab. Sending
     the reader to the console's own index is the same answer the product module
     routes give: the route simply is not there. */
  const activeId = activeIdFromPath(location.pathname);
  const activeKey = MODULE_GATED_SECTIONS[activeId];
  if (activeKey !== undefined && disabledKeys.has(activeKey)) {
    return <Navigate to={ADMIN_SECTION_ROUTES.members ?? "/admin"} replace />;
  }

  return (
    <SettingsLayout
      items={items}
      activeId={activeId}
      onNavigate={(id) => {
        /* Selecting the section you are already on is not leaving it, and the
           shared rail emits for the active entry too. Without this the guard
           would ask on a self-click, and answering Discard would clear the
           dirty registry while the draft stays mounted and visibly dirty —
           disarming the guard so the NEXT real move drops it silently, which
           is the very defect this change exists to close (review D-1). */
        if (id === activeId) return;
        const route = ADMIN_SECTION_ROUTES[id];
        if (route) navigate(route);
      }}
      title="Admin"
      subtitle="Tenant administration"
      navLabel="Admin sections"
      /* ONE lane for every section (PB-5178, Direction B). The per-section
         wide/default split it replaced moved the page edge between a form and
         a table; now every section renders in the 90rem console lane (PB-5216)
         — a fluid main column beside a meta aside (AdminSection) — and a wide
         table scrolls inside the main column instead of widening the page. */
      contentWidth="console"
      /* No pinned context strip (PB-5216). It carried the tenant's name and
         the reader's own name, and the chrome already states both — the
         wordmark top-left is the tenant, the avatar top-right is the reader —
         so on a single-tenant deployment it repeated two constants above all
         fourteen sections. If a tenant ever becomes something a reader can
         change, that belongs in the chrome as a switcher, not as a static line
         on the admin pages. */
    >
      <Outlet />
    </SettingsLayout>
  );
}

/* The leave guard wraps the rail and the section outlet together (PB-5403), so
   a rail click asks before it drops an unsaved admin draft. Same wiring the
   Settings host uses; a data router was never required, because the host
   consults the dirty registry itself rather than asking React Router to block. */
export function AdminPage(): ReactElement {
  return (
    <SettingsLeaveGuardProvider>
      <AdminPageHost />
    </SettingsLeaveGuardProvider>
  );
}
